import { useEffect, useRef } from 'react';
import { Input } from '@/components/shadcn/input.tsx';
import { Label } from '@/components/shadcn/label.tsx';
import { Textarea } from '@/components/shadcn/textarea.tsx';
import type { ValidationChange } from '@/modules/governmentIntake/base-entity.tsx';
import HeaderAndSub from '@/modules/governmentIntake/components/shared-component.tsx';
import { type ProjectsStructureData, useIntakeForm } from '@/modules/governmentIntake/context/intake-form-context.tsx';

export default function ProjectsSubStructure({ onValidationChange }: ValidationChange) {
  const { data, updateProjectsSubSection } = useIntakeForm();
  const raw = data.projects?.structure as Partial<ProjectsStructureData> | undefined;
  const structureData: ProjectsStructureData = {
    deptInvolved: raw?.deptInvolved ?? '',
    programsTrackedToday: raw?.programsTrackedToday ?? '',
    visibilityGaps: raw?.visibilityGaps ?? '',
    leadershipRequests: raw?.leadershipRequests ?? '',
  };

  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  useEffect(() => {
    onValidationChangeRef.current?.(true);
  }, []);

  const handleFieldChange = <K extends keyof ProjectsStructureData>(field: K, value: ProjectsStructureData[K]) => {
    updateProjectsSubSection('structure', { ...structureData, [field]: value });
  };

  return (
    <div className='flex flex-col flex-1 justify-center'>
      <header className='shrink-0 pt-4'>
        <HeaderAndSub title='Coordination & Visibility' subTitle='Understand cross-department coordination and visibility gaps.' />
      </header>

      <section className='flex-1 overflow-y-auto pb-4'>
        <div className='space-y-6 pt-8'>
          <div className='space-y-8'>
            <div className='space-y-3'>
              <Label className='block text-sm font-semibold text-gray-900 mb-2'>Departments involved in projects</Label>
              <Input
                value={structureData.deptInvolved}
                onChange={(e) => handleFieldChange('deptInvolved', e.target.value)}
                placeholder='eg., Planning, Public Works, Finance'
                className='w-full px-3 py-2'
              />
            </div>

            <div className='space-y-3'>
              <Label className='block text-sm font-semibold text-gray-900 mb-2'>How programs are tracked today</Label>
              <Input
                value={structureData.programsTrackedToday}
                onChange={(e) => handleFieldChange('programsTrackedToday', e.target.value)}
                placeholder='eg., Contractors, consultants, county agencies'
                className='w-full px-3 py-2'
              />
            </div>

            <div className='space-y-3'>
              <Label className='block text-sm font-semibold text-gray-900 mb-2'>Visibility gaps across departments</Label>
              <Textarea
                value={structureData.visibilityGaps}
                onChange={(e) => handleFieldChange('visibilityGaps', e.target.value)}
                rows={4}
                placeholder='Describe what information is siloed or hard to access across teams...'
                className='resize-none'
              />
            </div>

            <div className='space-y-3'>
              <Label className='block text-sm font-semibold text-gray-900 mb-2'>What leadership asks for that is hard to answer</Label>
              <Textarea
                value={structureData.leadershipRequests}
                onChange={(e) => handleFieldChange('leadershipRequests', e.target.value)}
                rows={4}
                placeholder='Questions or metrics leadership requests that are difficult to provide with current systems...'
                className='resize-none'
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
